/**
 * ProductsCard — product-mix breakdown for the OEM Home strip (Phase 2).
 *
 * Ports the reference HomeOEM "Products" card: a stacked horizontal mix bar
 * (Crop Circle orange ramp, darkest = largest share) over a list of rows
 * (colour swatch · name · units · %). REAL — assets grouped by their product
 * relationship. Consumes `useHomeCards(range)`.
 *
 * Map-plottable (mirrors RoutinesCard): pressing the header plots all located
 * assets, coloured by product; clicking a product row filters the map to that
 * product's assets. The point id is the asset id, so clicking a marker opens the
 * asset detail.
 */
import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { tokens, Skeleton, EmptyState, ErrorState } from '@gripp/shared-ui';
import { DashboardCardShell } from './dashboardCardShell';
import { useHomeCards } from '../hooks/useHomeCards';
import { HomeCardProps } from '../types';

// Orange ramp, darkest → lightest (largest share = darkest), matching the
// reference product-mix gradient.
const PRODUCT_COLORS = [
  tokens.colors.orange[700],
  tokens.colors.orange[600],
  tokens.colors.orange[500],
  tokens.colors.orange[400],
  tokens.colors.orange[300],
];

export const ProductsCard: React.FC<HomeCardProps> = ({
  range,
  selected,
  onToggleSelect,
  activeStatus,
  onSelectStatus,
}) => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useHomeCards(range);
  const products = data?.products;

  const title = t('home.products.title', 'Products');
  const action = undefined;

  if (isLoading) {
    return (
      <DashboardCardShell title={title} action={action} testID="products-card"
        loadingSlot={
          <View style={styles.loading}>
            <Skeleton height={16} radius={4} />
            {[0, 1, 2].map((i) => <Skeleton key={i} height={16} radius={4} />)}
          </View>
        }
      >
        {null}
      </DashboardCardShell>
    );
  }
  if (error) {
    return (
      <DashboardCardShell title={title} action={action} testID="products-card"
        errorSlot={<ErrorState title={t('home.cards.error', 'Could not load data')} testID="products-card-error" />}
      >
        {null}
      </DashboardCardShell>
    );
  }
  if (!products || products.items.length === 0) {
    return (
      <DashboardCardShell title={title} action={action} testID="products-card"
        emptySlot={<EmptyState title={t('home.products.empty', 'No products')} testID="products-card-empty" />}
      >
        {null}
      </DashboardCardShell>
    );
  }

  return (
    <DashboardCardShell
      title={title}
      subtitle={String(products.total)}
      action={action}
      testID="products-card"
      onHeaderPress={onToggleSelect}
      selected={selected}
      headerPressLabel={t('home.products.plot', 'Plot products on the map')}
    >
      <View style={styles.body}>
        {/* Stacked mix bar */}
        <View style={styles.mixBar}>
          {products.items.map((p, i) => (
            <View
              key={p.name}
              style={{
                flexGrow: p.percentage,
                flexBasis: 0,
                backgroundColor: PRODUCT_COLORS[i % PRODUCT_COLORS.length],
              }}
            />
          ))}
        </View>

        <ScrollView style={styles.list} showsVerticalScrollIndicator={false}>
          {products.items.map((p, i) => {
            const isActive = activeStatus === p.name;
            return (
              <Pressable
                key={p.name}
                style={[
                  styles.row,
                  isActive && styles.rowActive,
                  activeStatus && !isActive ? styles.rowDimmed : null,
                ]}
                onPress={() => onSelectStatus?.(p.name)}
                accessibilityRole="button"
                accessibilityLabel={t('home.products.filterBy', 'Plot {{name}} on the map', { name: p.name })}
              >
                <View
                  style={[
                    styles.swatch,
                    { backgroundColor: PRODUCT_COLORS[i % PRODUCT_COLORS.length] },
                  ]}
                />
                <Text style={[styles.name, isActive && styles.nameActive]} numberOfLines={1}>{p.name}</Text>
                <Text style={styles.units}>{p.units}</Text>
                <Text style={styles.pct}>{p.percentage}%</Text>
              </Pressable>
            );
          })}
        </ScrollView>
      </View>
    </DashboardCardShell>
  );
};

ProductsCard.displayName = 'ProductsCard';

const styles = StyleSheet.create({
  loading: {
    gap: tokens.spacing.sm,
  },
  body: {
    flex: 1,
  },
  mixBar: {
    flexDirection: 'row',
    height: 16,
    borderRadius: 4,
    overflow: 'hidden',
    gap: 2,
    marginBottom: tokens.spacing.sm,
  },
  list: {
    flex: 1,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xxs,
  },
  rowActive: {
    backgroundColor: tokens.colors.slate[100],
    borderRadius: tokens.borderRadius.sm,
  },
  rowDimmed: {
    opacity: 0.45,
  },
  swatch: {
    width: 10,
    height: 10,
    borderRadius: 2,
  },
  name: {
    ...tokens.typography.label.medium,
    fontSize: 13,
    lineHeight: 18,
    color: tokens.colors.neutral[800],
    flex: 1,
  },
  nameActive: {
    fontWeight: tokens.typography.label.mediumEmphasised.fontWeight,
  },
  units: {
    ...tokens.typography.label.medium,
    color: tokens.colors.slate[400],
  },
  pct: {
    ...tokens.typography.label.medium,
    fontWeight: '600',
    color: tokens.colors.neutral[800],
    width: 36,
    textAlign: 'right',
    fontVariant: ['tabular-nums'],
  },
});
